import { AzureStorageUploadResult, BaseDto, FileInfo, ItemResult, SaSTokenInfoDto } from '../../types';
import { AbstractValidator } from 'fluent-ts-validator';
import i18n from '../../../i18n';
import { AsyncAction, ReduxRepository, repository } from 'redux-scaffolding-ts';
import { DataStore } from '../../dataStore';
import { FormStore } from '../../formStore';
import { AxiosResponse } from 'axios';
import { ToastComponent } from 'site/pages/landing-pages/util/toast-component';
import { container } from 'inversify.config';
import HttpService from 'services/http-service';
import * as AzureStorageNpm from 'azure-storage';
import { DateTimeService } from 'services/datetime-service';

export interface RestrictionWarningDto extends BaseDto {
  id: string;
  ttcLocationId: string;
  ttcLocationName: string;
  travelingLocationId: string;
  travelingLocationName: string;
  visaId: string;
  visaName: string;
  visaApplicationDuration: number;
  isRestriction: boolean;
  comments: string;
  contractId: string;
  contractName: string;
  flightCostId: string;
  flightCostName: string;
}

export interface CreateRestrictionWarningDto {
  TTCLocationId: string;
  TravelingLocationId: string;
  VisaId: string;
  VisaApplicationDuration: number;
  IsRestriction: boolean;
  Comments: string;
  ContractId: string;
  FlightCostId: string;
}

export interface ChangeRestrictionWarningDto {
  Id: string;
  TTCLocationId: string;
  TravelingLocationId: string;
  VisaId: string;
  VisaApplicationDuration: number;
  IsRestriction: boolean;
  Comments: string;
  ContractId: string;
  FlightCostId: string;
}

export interface RestrictionWarningDownloaderState {
  isBusy: boolean;
  result: ItemResult<any>;
}

export interface RestrictionWarningUploaderState {
  isBusy: boolean;
  result: AzureStorageUploadResult;
}

export class RestrictionWarningValidator extends AbstractValidator<RestrictionWarningDto> {
  constructor() {
    super();

    this.validateIfString(o => o.id)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Id is required')));

    this.validateIfString(o => o.ttcLocationId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('TTC Location is required')));

    this.validateIfString(o => o.travelingLocationId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Traveling Location is required')));

    this.validateIfString(o => o.visaId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Visa Requirement is required')));

    this.validateIfNumber(o => o.visaApplicationDuration)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Visa application. Months duration is required')));

    this.validateIfNumber(o => o.visaApplicationDuration)
      .isDefined()
      .isNotNull()
      .isGreaterThanOrEqual(0)
      .withFailureMessage(String(i18n.t('Visa application. Months duration is lower than 0 months')));

    this.validateIfString(o => o.comments)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Comments is required')));

    this.validateIfString(o => o.contractId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Contract is required')));

    this.validateIfString(o => o.flightCostId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Flight Cost is required')));
  }
}

export class CreateRestrictionWarningValidator extends AbstractValidator<CreateRestrictionWarningDto> {
  constructor() {
    super();

    this.validateIfString(o => o.TTCLocationId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('TTC Location is required')));

    this.validateIfString(o => o.TravelingLocationId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Traveling Location is required')));

    this.validateIfString(o => o.VisaId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Visa Requirement is required')));

    this.validateIfNumber(o => o.VisaApplicationDuration)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Visa application. Months duration is required')));

    this.validateIfNumber(o => o.VisaApplicationDuration)
      .isDefined()
      .isNotNull()
      .isGreaterThanOrEqual(0)
      .withFailureMessage(String(i18n.t('Visa application. Months duration is lower than 0 months')));

    this.validateIfString(o => o.Comments)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Comments is required')));

    this.validateIfString(o => o.ContractId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Contract is required')));

    this.validateIfString(o => o.FlightCostId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Flight Cost is required')));

    this.validateIf(o => o)
      .isNotNull()
      .fulfills(o => o.TTCLocationId && o.TravelingLocationId && o.TTCLocationId !== o.TravelingLocationId)
      .withFailureMessage(String(i18n.t('TTC and Traveling locations must be differents')));
  }
}

export class ChangeRestrictionWarningValidator extends AbstractValidator<ChangeRestrictionWarningDto> {
  constructor() {
    super();

    this.validateIfString(o => o.Id)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Id is required')));

    this.validateIfString(o => o.TTCLocationId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('TTC Location is required')));

    this.validateIfString(o => o.TravelingLocationId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Traveling Location is required')));

    this.validateIfString(o => o.VisaId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Visa Requirement is required')));

    this.validateIfNumber(o => o.VisaApplicationDuration)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Visa application. Months duration is required')));

    this.validateIfNumber(o => o.VisaApplicationDuration)
      .isDefined()
      .isNotNull()
      .isGreaterThanOrEqual(0)
      .withFailureMessage(String(i18n.t('Visa application. Months duration is lower than 0 months')));

    this.validateIfString(o => o.Comments)
      .isDefined()
      .isNotNull()
      .isNotEmpty()
      .withFailureMessage(String(i18n.t('Comments is required')));

    this.validateIfString(o => o.ContractId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Contract is required')));

    this.validateIfString(o => o.FlightCostId)
      .isNotEmpty()
      .isUuid('4')
      .withFailureMessage(String(i18n.t('Flight Cost is required')));

    this.validateIf(o => o)
      .isNotNull()
      .fulfills(o => o.TTCLocationId && o.TravelingLocationId && o.TTCLocationId !== o.TravelingLocationId)
      .withFailureMessage(String(i18n.t('TTC and Traveling locations must be differents')));
  }
}

@repository('@@RESTRICTIONSWARNINGS', 'restrictionWarnings.summary')
export class RestrictionWarningsStore extends DataStore<RestrictionWarningDto> {
  baseUrl = 'master-data/v1';
  createPath = 'new-restriction';
  retrievePath = 'get-restrictions';
  updatePath = 'update-restriction';
  deletePath = 'delete-restriction';

  protected validate(item: RestrictionWarningDto) {
    return new RestrictionWarningValidator().validate(item);
  }

  constructor() {
    super('RESTRICTIONWARNING', {
      isBusy: false,
      items: [],
      count: 0,
      result: undefined,
      discard: item => {}
    });
  }
}

@repository('@@RESTRICTIONSWARNINGS', 'restrictionWarnings.new')
export class NewRestrictionWarningStore extends FormStore<CreateRestrictionWarningDto> {
  baseUrl = 'master-data/v1';
  createPath = 'new-restriction';
  retrievePath = 'get-restrictions';
  updatePath = 'update-restriction';

  protected validate(item: CreateRestrictionWarningDto) {
    return new CreateRestrictionWarningValidator().validate(item);
  }

  constructor() {
    super('NEW_RESTRICTIONWARNING', {
      isBusy: false,
      status: 'New',
      item: undefined,
      result: undefined
    });
  }
}

@repository('@@RESTRICTIONSWARNINGS', 'restrictionWarning.change')
export class ChangeRestrictionWarningStore extends FormStore<ChangeRestrictionWarningDto> {
  baseUrl = 'master-data/v1';
  createPath = 'new-restriction';
  retrievePath = 'get-restrictions';
  updatePath = 'update-restriction';

  protected validate(item: ChangeRestrictionWarningDto) {
    return new ChangeRestrictionWarningValidator().validate(item);
  }

  constructor() {
    super('CHANGE_RESTRICTIONWARNING', {
      isBusy: false,
      status: 'Modified',
      item: undefined,
      result: undefined
    });
  }
}

@repository('@@RESTRICTIONSWARNINGS', 'restrictionWarning.download')
export class DownloadRestrictionWarningStore extends ReduxRepository<RestrictionWarningDownloaderState> {
  //baseUrl = 'http://localhost:7072/api/v1';
  baseUrl = 'master-data/v1';
  initBackgroundDownloadUrl = 'download-restriction-warning-in-background';

  public BACKGROUND_DOWNLOAD_INITIALIZED = 'BACKGROUND_DOWNLOAD_INITIALIZED';

  constructor() {
    super({
      isBusy: false,
      result: undefined
    });

    this.onBackgroundDownloadInitilized.bind(this);

    this.addReducer(this.BACKGROUND_DOWNLOAD_INITIALIZED, this.onBackgroundDownloadInitilized, 'AsyncAction');
  }

  public async initBackgroundDownload() {
    const url = `${this.baseUrl}/${this.initBackgroundDownloadUrl}`;

    const httpService = container.get<HttpService>(HttpService);
    return this.dispatchAsync(this.BACKGROUND_DOWNLOAD_INITIALIZED, httpService.post(url, null));
  }

  private onBackgroundDownloadInitilized = (): AsyncAction<AxiosResponse<any>, RestrictionWarningDownloaderState> => {
    return {
      onStart: () => ({ ...this.state, isBusy: true, result: undefined }),
      onSuccess: result => {
        ToastComponent({ text: String(i18n.t('The email has been sent')), type: 'success-toast' });
        return { ...this.state, isBusy: false, result: result.data };
      },
      onError: error => {
        ToastComponent({ text: String(i18n.t('File export failed')), type: 'error-toast' });
        return {
          ...this.state,
          isBusy: false,
          result: error && error.response && error.response.data && error.response.data.messages ? error.response.data : error
        };
      }
    };
  };
}

@repository('@@RESTRICTIONSWARNINGS', 'restrictionWarning.upload')
export class UploadRestrictionWarningStore extends ReduxRepository<RestrictionWarningUploaderState> {
  //baseUrl = 'http://localhost:7072/api/v1';
  baseUrl = 'master-data/v1';
  getSaSTokenUrl = 'get-restrictions-blob-sas-url';
  downloadTemplateUrl = 'get-restrictions-import-template';

  public SAS_TOKEN_OBTAINED = 'SAS_TOKEN_OBTAINED';
  public FILE_UPLOADED = 'FILE_UPLOADED';
  public TEMPLATE_DOWNLOADED = 'TEMPLATE_DOWNLOADED';

  constructor() {
    super({
      isBusy: false,
      result: undefined
    });

    this.uploadFile.bind(this);
    this.onSaSTokenObtained.bind(this);
    this.onFileUploaded.bind(this);
    this.onTemplateDownloaded.bind(this);

    this.addReducer(this.SAS_TOKEN_OBTAINED, this.onSaSTokenObtained, 'AsyncAction');
    this.addReducer(this.FILE_UPLOADED, this.onFileUploaded, 'Simple');
    this.addReducer(this.TEMPLATE_DOWNLOADED, this.onTemplateDownloaded, 'AsyncAction');
  }

  public async uploadFile(fileInfo: FileInfo) {
    const { fileName, fileSize } = fileInfo;
    const params = { fileName, fileSize };
    const url = `${this.baseUrl}/${this.getSaSTokenUrl}`;

    const httpService = container.get<HttpService>(HttpService);
    return this.dispatchAsync(this.SAS_TOKEN_OBTAINED, httpService.get<ItemResult<SaSTokenInfoDto>>(url, params), fileInfo);
  }

  private onSaSTokenObtained = (): AsyncAction<AxiosResponse<ItemResult<SaSTokenInfoDto>>, RestrictionWarningUploaderState> => {
    return {
      onStart: () => ({ ...this.state, isBusy: true, result: undefined }),
      onSuccess: (result: AxiosResponse<ItemResult<SaSTokenInfoDto>>, fileInfo: FileInfo) => {
        let data = result.data.item;
        const blobService = AzureStorage.Blob.createBlobServiceWithSas(data.host, data.saSToken).withFilter(
          new AzureStorageNpm.ExponentialRetryPolicyFilter(5)
        );
        const customBlockSize = fileInfo.fileSize > 1024 * 1024 * 32 ? 1024 * 1024 * 4 : 1024 * 512;
        blobService.singleBlobPutThresholdInBytes = customBlockSize;

        blobService.createBlockBlobFromBrowserFile(
          data.container,
          data.filePath,
          fileInfo.content,
          {
            blockSize: customBlockSize,
            metadata: {
              originalName: encodeURIComponent(fileInfo.fileName),
              userId: fileInfo.userId,
              userName: fileInfo.userName,
              timestamp: DateTimeService.toString(DateTimeService.now())
            }
          },
          (error: Error, _: AzureStorageNpm.BlobService.BlobResult, response: AzureStorageNpm.ServiceResponse) => {
            const res = { error, response };
            this.dispatch(this.FILE_UPLOADED, res);
          }
        );
        return { ...this.state };
      },
      onError: error => ({
        ...this.state,
        isBusy: false,
        result:
          error && error.response && error.response.data && error.response.data.messages
            ? {
                error: { message: error.response.data.messages.map(x => x.body).join('.') } as Error,
                response: undefined as AzureStorageNpm.ServiceResponse
              }
            : { error: undefined as Error, response: { isSuccessful: false } as AzureStorageNpm.ServiceResponse }
      })
    };
  };

  protected onFileUploaded = (result: AzureStorageUploadResult): RestrictionWarningUploaderState => {
    return { ...this.state, isBusy: false, result };
  };

  public async downloadTemplate() {
    const url = `${this.baseUrl}/${this.downloadTemplateUrl}`;

    const httpService = container.get<HttpService>(HttpService);
    return this.dispatchAsync(this.TEMPLATE_DOWNLOADED, httpService.download(url, 'Restrictions and Warnings.xlsx'));
  }

  private onTemplateDownloaded = (): AsyncAction<AxiosResponse<any>, RestrictionWarningUploaderState> => {
    return {
      onStart: () => ({ ...this.state, isBusy: true, result: undefined }),
      onSuccess: () => ({ ...this.state, isBusy: false, result: undefined }),
      onError: error => ({
        ...this.state,
        isBusy: false,
        result:
          error && error.response && error.response.data && error.response.data.messages
            ? {
                error: { message: error.response.data.messages.map(x => x.body).join('.') } as Error,
                response: undefined as AzureStorageNpm.ServiceResponse
              }
            : { error: undefined as Error, response: { isSuccessful: false } as AzureStorageNpm.ServiceResponse }
      })
    };
  };
}
